import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@agape/shared";

/**
 * Agape brand mark — a rounded "sanctuary" tile with a heart (agape = love),
 * rendered in the signature terracotta→candle-gold gradient. Warm, calm and
 * legible at any size; uses currentColor-independent brand stops so it reads
 * the same in light and dark. The gradient id is suffixed to stay unique when
 * multiple logos render on the same page (e.g. sidebar + auth).
 */
let markCounter = 0;

function LogoMark(props: ComponentProps<"svg">) {
  const gradId = `agape-mark-${(markCounter += 1)}`;
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={gradId} x1="4" y1="3" x2="28" y2="29" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.58 0.16 40)" />
          <stop offset="1" stop-color="oklch(0.74 0.13 80)" />
        </linearGradient>
      </defs>
      {/* Rounded sanctuary tile */}
      <rect x="4" y="3" width="24" height="26" rx="7" fill={`url(#${gradId})`} />
      {/* Heart — ágape */}
      <path
        d="M16 22.6c-3.9-2.9-6.1-5-6.1-7.7 0-1.9 1.5-3.4 3.4-3.4 1.1 0 2.1 0.5 2.7 1.4 0.6-0.9 1.6-1.4 2.7-1.4 1.9 0 3.4 1.5 3.4 3.4 0 2.7-2.2 4.8-6.1 7.7Z"
        fill="white"
      />
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0 drop-shadow-sm" />
          <span class="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/70">
            Ágape
          </span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 shrink-0 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic
            component={wordmarkTag()}
            class="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/80"
          >
            Ágape
          </Dynamic>
        </>
      )}
    </div>
  );
}
